// src/pages/league-dashboard/LeagueOwners.js - RESTORED with inline editing + invite form
import React, { useState } from 'react';
import { Users, Crown, Send, Edit, Trash2, Save, X } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';

const LeagueOwners = ({ league, leagueId, user, owners, ownersLoading, loadLeagueData }) => {
  
  // 🔍 DEBUG: Log owners data whenever it changes
  React.useEffect(() => {
    console.log('🔍 DEBUG: Owners data updated:', {
      'owners.length': owners.length,
      'first 3 owners': owners.slice(0, 3).map(o => ({
        slot: o.slot,
        team_id: o.team_id,
        user_id: o.user_id,
        owner_email: o.owner_email,
        owner_name: o.owner_name,
        status: o.status
      }))
    });
  }, [owners]);
  
  // ========================================
  // LOCAL STATE
  // ========================================
  const [inviteForm, setInviteForm] = useState({
    ownerName: '',
    ownerEmail: '',
    personalMessage: '',
    targetSlot: null,
    targetTeamId: null,
    targetTeamName: null
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  
  // Inline editing state per row
  const [editingRows, setEditingRows] = useState({}); // { slot: { team_name: '', owner_name: '', owner_email: '' } }
  const [savingRows, setSavingRows] = useState({}); // { slot: true/false }

  // ========================================
  // CALCULATED VALUES
  // ========================================
  const maxTeams = league?.league_settings?.max_teams || 12;
  const activeTeamsCount = owners.filter(owner => owner.status === 'Active').length;
  const pendingInvitesCount = owners.filter(owner => owner.status === 'Pending').length;
  const openSlots = owners.filter(owner => owner.status === 'Open').length;

  // ========================================
  // INLINE EDITING HANDLERS
  // ========================================
  
  const handleStartEdit = (owner) => {
    console.log('🔍 DEBUG handleStartEdit called:', {
      'owner.slot': owner.slot,
      'owner.team_name': owner.team_name,
      'owner.owner_name': owner.owner_name,
      'owner.owner_email': owner.owner_email,
      'owner.team_id': owner.team_id,
      'owner.user_id': owner.user_id
    });
    
    const newEditingData = {
      ...editingRows,
      [owner.slot]: {
        team_name: owner.team_name || '',
        owner_name: owner.owner_name || '',
        owner_email: owner.owner_email || ''
      }
    };
    
    console.log('🔍 DEBUG setEditingRows with:', newEditingData);
    setEditingRows(newEditingData);
  };

  const handleCancelEdit = (slot) => {
    const newEditing = {...editingRows};
    delete newEditing[slot];
    setEditingRows(newEditing);
  };

  const handleFieldChange = (slot, field, value) => {
    setEditingRows({
      ...editingRows,
      [slot]: {
        ...editingRows[slot],
        [field]: value
      }
    });
  };

  const handleSaveRow = async (owner) => {
    console.log('🔍 DEBUG handleSaveRow START:', {
      'owner.slot': owner.slot,
      'editingRows': editingRows,
      'editingRows[owner.slot]': editingRows[owner.slot]
    });
    
    const rowData = editingRows[owner.slot];
    
    if (!rowData) {
      console.error('❌ ERROR: No rowData! editingRows[' + owner.slot + '] is undefined!');
      return;
    }

    try {
      setSavingRows({...savingRows, [owner.slot]: true});
      setInviteStatus(null);

      // 🔍 DEBUG: Log the owner state
      console.log('🔍 DEBUG handleSaveRow:', {
        'owner.team_id': owner.team_id,
        'owner.user_id': owner.user_id,
        'owner.status': owner.status,
        'owner.slot': owner.slot,
        'rowData': rowData,
        'Condition 1 (!owner.team_id && rowData.team_name.trim())': (!owner.team_id && rowData.team_name.trim()),
        'Condition 2 (owner.team_id && !owner.user_id)': (owner.team_id && !owner.user_id),
        'Condition 3 (owner.team_id && owner.user_id)': (owner.team_id && owner.user_id)
      });

      // CASE 1: No team exists yet - create team, then update with owner details
      if (!owner.team_id && rowData.team_name.trim()) {
        const createResponse = await leaguesAPI.createManualTeam(leagueId, rowData.team_name.trim());

        if (createResponse.success) {
          const newTeamId = createResponse.team?.team_id || createResponse.team_id;

          // If user also provided owner name or email, update the team immediately
          if (newTeamId && (rowData.owner_name.trim() || rowData.owner_email.trim())) {
            try {
              await leaguesAPI.updateTeamInfo(leagueId, newTeamId, {
                team_name: rowData.team_name.trim(),
                owner_name: rowData.owner_name.trim() || 'Awaiting Owner',
                owner_email: rowData.owner_email.trim() || ''
              });
            } catch (updateErr) {
              console.warn('Team created but failed to update owner details:', updateErr);
              // Don't fail the whole operation — team was created
            }
          }

          const hasEmail = rowData.owner_email.trim();
          setInviteStatus({
            type: 'success',
            message: hasEmail
              ? `Team "${createResponse.team_name || rowData.team_name.trim()}" created with owner details! Click "Invite" to send the invitation.`
              : `Team "${createResponse.team_name || rowData.team_name.trim()}" created! Add an email and save again to invite.`
          });

          handleCancelEdit(owner.slot);

          await new Promise(resolve => setTimeout(resolve, 500));
          await loadLeagueData();
        } else {
          throw new Error(createResponse.message || 'Failed to create team');
        }
      }
      // CASE 2: Team exists but no owner - UPDATE the team record (don't send invitation yet)
      else if (owner.team_id && !owner.user_id) {
        // Validate email if provided
        if (rowData.owner_email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(rowData.owner_email)) {
            throw new Error('Please enter a valid email address');
          }

          const existingOwner = owners.find(o => 
            o.owner_email === rowData.owner_email.trim() && 
            o.slot !== owner.slot &&
            (o.status === 'Active' || o.status === 'Pending')
          );
          if (existingOwner) {
            throw new Error('This email is already associated with another team in this league');
          }
        }

        // UPDATE the team record with new info (no invitation sent)
        console.log('🔄 CASE 2: Updating team without owner - calling updateTeamInfo...');
        const response = await leaguesAPI.updateTeamInfo(leagueId, owner.team_id, {
          team_name: rowData.team_name.trim() || owner.team_name,
          owner_name: rowData.owner_name.trim() || 'Awaiting Owner',
          owner_email: rowData.owner_email.trim() || 'pending@dynastydugout.com'
        });

        console.log('✅ updateTeamInfo response:', response);
        
        if (response.success) {
          setInviteStatus({
            type: 'success',
            message: 'Team information updated successfully!'
          });
          
          handleCancelEdit(owner.slot);
          
          // CRITICAL FIX: Add delay to ensure backend has committed changes
          console.log('⏳ Waiting 500ms before refreshing data...');
          await new Promise(resolve => setTimeout(resolve, 500));
          
          console.log('🔄 Calling loadLeagueData to refresh...');
          await loadLeagueData();
          
          console.log('✅ Data refresh complete - UI should update now');
        } else {
          throw new Error(response.message || 'Failed to update team');
        }
      }
      // CASE 3: Team exists WITH owner (Active team) - UPDATE the team record
      else if (owner.team_id && owner.user_id) {
        // Validate email if provided
        if (rowData.owner_email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(rowData.owner_email)) {
            throw new Error('Please enter a valid email address');
          }

          const existingOwner = owners.find(o => 
            o.owner_email === rowData.owner_email.trim() && 
            o.slot !== owner.slot &&
            (o.status === 'Active' || o.status === 'Pending')
          );
          if (existingOwner) {
            throw new Error('This email is already associated with another team in this league');
          }
        }

        // UPDATE the team record with new info
        const response = await leaguesAPI.updateTeamInfo(leagueId, owner.team_id, {
          team_name: rowData.team_name.trim() || owner.team_name,
          owner_name: rowData.owner_name.trim() || owner.owner_name,
          owner_email: rowData.owner_email.trim() || owner.owner_email
        });

        if (response.success) {
          setInviteStatus({
            type: 'success',
            message: 'Team information updated successfully! The new email has been saved.'
          });
          
          handleCancelEdit(owner.slot);
          
          // Add delay to ensure backend has committed changes
          await new Promise(resolve => setTimeout(resolve, 500));
          await loadLeagueData();
        } else {
          throw new Error(response.message || 'Failed to update team');
        }
      }
      else {
        throw new Error('Please fill in the required fields');
      }

    } catch (error) {
      console.error('Error saving row:', error);
      setInviteStatus({
        type: 'error',
        message: error.message || 'Failed to save. Please try again.'
      });
    } finally {
      setSavingRows({...savingRows, [owner.slot]: false});
    }
  };

  // ========================================
  // INVITE FORM HANDLERS
  // ========================================
  
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.ownerName.trim() || !inviteForm.ownerEmail.trim()) {
      setInviteStatus({
        type: 'error',
        message: 'Please fill in both owner name and email address.'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.ownerEmail)) {
      setInviteStatus({
        type: 'error', 
        message: 'Please enter a valid email address.'
      });
      return;
    }

    const existingOwner = owners.find(owner => 
      owner.owner_email === inviteForm.ownerEmail.trim() && 
      (owner.status === 'Active' || owner.status === 'Pending')
    );
    if (existingOwner) {
      setInviteStatus({
        type: 'error',
        message: 'This email address is already associated with this league.'
      });
      return;
    }

    try {
      setInviteLoading(true);
      setInviteStatus(null);

      let response;
      
      if (inviteForm.targetTeamId) {
        response = await leaguesAPI.inviteOwnerToTeam(leagueId, inviteForm.targetTeamId, {
          ownerName: inviteForm.ownerName.trim(),
          ownerEmail: inviteForm.ownerEmail.trim(),
          personalMessage: inviteForm.personalMessage.trim(),
          leagueName: league?.league_name,
          commissionerName: user?.given_name || user?.firstName || 'League Commissioner',
          teamName: inviteForm.targetTeamName
        });
      } else {
        response = await leaguesAPI.inviteOwner(leagueId, {
          ownerName: inviteForm.ownerName.trim(),
          ownerEmail: inviteForm.ownerEmail.trim(),
          personalMessage: inviteForm.personalMessage.trim(),
          leagueName: league?.league_name,
          commissionerName: user?.given_name || user?.firstName || 'League Commissioner',
          targetSlot: inviteForm.targetSlot
        });
      }

      if (response.success) {
        setInviteStatus({
          type: 'success',
          message: inviteForm.targetTeamId 
            ? `Invitation sent to ${inviteForm.ownerName} for ${inviteForm.targetTeamName}!`
            : `Invitation sent successfully to ${inviteForm.ownerName} (${inviteForm.ownerEmail})`
        });
        
        setInviteForm({
          ownerName: '',
          ownerEmail: '',
          personalMessage: '',
          targetSlot: null,
          targetTeamId: null,
          targetTeamName: null
        });
        
        loadLeagueData();
        
      } else {
        throw new Error(response.message || 'Failed to send invitation');
      }

    } catch (error) {
      console.error('Error sending invitation:', error);
      setInviteStatus({
        type: 'error',
        message: error.message || 'Failed to send invitation. Please try again.'
      });
    } finally {
      setInviteLoading(false);
    }
  };

  // ========================================
  // OTHER HANDLERS
  // ========================================
  
  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await leaguesAPI.cancelInvitation(leagueId, invitationId);
      
      if (response.success) {
        setInviteStatus({
          type: 'success',
          message: 'Invitation cancelled successfully.'
        });
        
        loadLeagueData();
      } else {
        throw new Error(response.message || 'Failed to cancel invitation');
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setInviteStatus({
        type: 'error',
        message: 'Failed to cancel invitation. Please try again.'
      });
    }
  };

  const handleToggleCommissioner = async (teamId, currentStatus) => {
    const action = currentStatus ? 'remove' : 'grant';
    if (!window.confirm(`Are you sure you want to ${action} commissioner rights?`)) {
      return;
    }
    
    try {
      const response = await leaguesAPI.toggleCommissionerStatus(leagueId, teamId, !currentStatus);
      
      if (response.success) {
        setInviteStatus({
          type: 'success',
          message: `Commissioner rights ${currentStatus ? 'removed' : 'granted'} successfully.`
        });
        loadLeagueData();
      } else {
        throw new Error(response.message || 'Failed to update commissioner status');
      }
    } catch (error) {
      console.error('Error updating commissioner status:', error);
      setInviteStatus({
        type: 'error',
        message: 'Failed to update commissioner rights.'
      });
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete "${teamName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // TODO: Call API to delete team
      setInviteStatus({
        type: 'error',
        message: 'Team deletion not yet implemented.'
      });
    } catch (error) {
      console.error('Error deleting team:', error);
      setInviteStatus({
        type: 'error',
        message: 'Failed to delete team. Please try again.'
      });
    }
  };

  const handleSendInvite = async (owner) => {
    // Validate that owner has a valid email
    if (!owner.owner_email || owner.owner_email === 'pending@dynastydugout.com' || owner.owner_email === 'N/A') {
      setInviteStatus({
        type: 'error',
        message: 'Please add a valid owner email before sending invitation.'
      });
      return;
    }
    
    // 🔧 FIX: Derive owner name from email if it's still "Awaiting Owner"
    let ownerNameToSend = owner.owner_name;
    if (!ownerNameToSend || ownerNameToSend === 'Awaiting Owner') {
      // Extract name from email (everything before @)
      ownerNameToSend = owner.owner_email.split('@')[0];
      console.log('🔧 Derived owner name from email:', ownerNameToSend);
    }

    try {
      setSavingRows({...savingRows, [owner.slot]: true});
      setInviteStatus(null);

      const response = await leaguesAPI.inviteOwnerToTeam(leagueId, owner.team_id, {
        ownerName: ownerNameToSend,
        ownerEmail: owner.owner_email,
        personalMessage: '',
        leagueName: league?.league_name,
        commissionerName: user?.given_name || user?.firstName || 'League Commissioner',
        teamName: owner.team_name
      });

      if (response.success) {
        setInviteStatus({
          type: 'success',
          message: `Invitation sent to ${ownerNameToSend} (${owner.owner_email}) for ${owner.team_name}!`
        });
        
        // Add delay to ensure backend has committed changes
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadLeagueData();
      } else {
        throw new Error(response.message || 'Failed to send invitation');
      }

    } catch (error) {
      console.error('Error sending invitation:', error);
      setInviteStatus({
        type: 'error',
        message: error.message || 'Failed to send invitation. Please try again.'
      });
    } finally {
      setSavingRows({...savingRows, [owner.slot]: false});
    }
  };

  // ========================================
  // RENDER HELPERS
  // ========================================
  
  const renderEditableCell = (owner, field, placeholder) => {
    const isEditing = editingRows[owner.slot];
    const value = isEditing ? editingRows[owner.slot][field] : owner[field] || '';
    
    if (isEditing) {
      return (
        <input
          type={field === 'owner_email' ? 'email' : 'text'}
          value={editingRows[owner.slot][field]}
          onChange={(e) => handleFieldChange(owner.slot, field, e.target.value)}
          placeholder={placeholder}
          className={`${dynastyTheme.components.input} text-sm py-1`}
          autoFocus={field === 'team_name'}
        />
      );
    }

    if (value) {
      return (
        <span className={dynastyTheme.classes.text.white}>
          {value}
        </span>
      );
    }

    return (
      <span className={`${dynastyTheme.classes.text.neutralLighter} italic`}>
        {placeholder}
      </span>
    );
  };

  const renderActionButtons = (owner) => {
    const isEditing = editingRows[owner.slot];
    const isSaving = savingRows[owner.slot];

    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => handleSaveRow(owner)}
            disabled={isSaving}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'xs')} flex items-center space-x-1`}
            title="Save changes"
          >
            {isSaving ? (
              <div className={`w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin`} />
            ) : (
              <Save className="w-3 h-3" />
            )}
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
          <button 
            onClick={() => handleCancelEdit(owner.slot)}
            disabled={isSaving}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'xs')}
            title="Cancel editing"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        {owner.status === 'Pending' && owner.invitation_id && (
          <button 
            onClick={() => handleCancelInvitation(owner.invitation_id)}
            className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'xs')} ${dynastyTheme.classes.text.error}`}
            title="Cancel invitation"
          >
            Cancel
          </button>
        )}

        {/* Show different buttons based on team state */}
        {!owner.team_id && owner.status === 'Open' && (
          <button 
            onClick={() => handleStartEdit(owner)}
            className={dynastyTheme.utils.getComponent('button', 'primary', 'xs')}
            title="Create team"
          >
            <Edit className="w-3 h-3 mr-1" />
            Create
          </button>
        )}

        {owner.team_id && !owner.user_id && (
          // Team exists but no owner yet
          <>
            {(() => {
              const hasValidEmail = owner.owner_email && owner.owner_email !== 'pending@dynastydugout.com' && owner.owner_email !== 'N/A';
              const hasValidName = owner.owner_name && owner.owner_name !== 'Awaiting Owner';
              // 🔧 FIX: Show Invite button with just email - we'll derive name from email if needed
              const shouldShowInvite = hasValidEmail;
              
              console.log(`🔍 INVITE BUTTON CHECK for slot ${owner.slot}:`, {
                'owner.owner_email': owner.owner_email,
                'hasValidEmail': hasValidEmail,
                'owner.owner_name': owner.owner_name,
                'hasValidName': hasValidName,
                'shouldShowInvite': shouldShowInvite,
                '⚠️ NOTE': hasValidEmail && !hasValidName ? 'Will derive name from email' : 'OK'
              });
              
              return shouldShowInvite;
            })() ? (
              // Has valid email AND name - show Invite button to send invitation
              <button 
                onClick={() => handleSendInvite(owner)}
                disabled={savingRows[owner.slot]}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'xs')}
                title="Send invitation to this owner"
              >
                {savingRows[owner.slot] ? (
                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-3 h-3 mr-1" />
                )}
                {savingRows[owner.slot] ? 'Sending...' : 'Invite'}
              </button>
            ) : null}
            
            <button 
              onClick={() => handleStartEdit(owner)}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'xs')}
              title="Edit team details"
            >
              <Edit className="w-3 h-3" />
            </button>
          </>
        )}

        {owner.status === 'Active' && owner.team_id && (
          <>
            <button 
              onClick={() => handleStartEdit(owner)}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'xs')}
              title="Edit team"
            >
              <Edit className="w-3 h-3" />
            </button>
            {!owner.is_commissioner && (
              <button 
                onClick={() => handleDeleteTeam(owner.team_id, owner.team_name)}
                className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'xs')} ${dynastyTheme.classes.text.error}`}
                title="Delete team"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <Users className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
          <div>
            <h2 className={dynastyTheme.components.heading.h2}>
              League Owners
            </h2>
            <p className={dynastyTheme.classes.text.neutralLight}>
              Manage team ownership and send invitations to new owners
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.success}`}>{activeTeamsCount}</div>
            <div className={dynastyTheme.classes.text.neutralLight}>Active Teams</div>
          </div>
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.warning}`}>{pendingInvitesCount}</div>
            <div className={dynastyTheme.classes.text.neutralLight}>Pending Invitations</div>
          </div>
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.primary}`}>{openSlots}</div>
            <div className={dynastyTheme.classes.text.neutralLight}>Open Slots</div>
          </div>
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>{maxTeams}</div>
            <div className={dynastyTheme.classes.text.neutralLight}>Total Slots</div>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {inviteStatus && (
        <div className={`p-4 rounded-lg ${
          inviteStatus.type === 'success' 
            ? dynastyTheme.classes.bg.success 
            : dynastyTheme.classes.bg.error
        }`}>
          <p className={dynastyTheme.classes.text.white}>{inviteStatus.message}</p>
        </div>
      )}

      {/* RESTORED: Invitation Form */}
      <div id="invite-form-section" className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-4`}>
          {inviteForm.targetTeamName 
            ? `Invite Owner for ${inviteForm.targetTeamName}` 
            : inviteForm.targetSlot 
              ? `Invite New Owner for Team ${inviteForm.targetSlot}` 
              : 'Invite New Owner'
          }
        </h3>

        <form onSubmit={handleInviteSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={dynastyTheme.components.label}>
                Owner Name *
              </label>
              <input
                type="text"
                value={inviteForm.ownerName}
                onChange={(e) => setInviteForm({...inviteForm, ownerName: e.target.value})}
                placeholder="Enter the new owner's full name"
                className={dynastyTheme.components.input}
                required
              />
            </div>
            
            <div>
              <label className={dynastyTheme.components.label}>
                Email Address *
              </label>
              <input
                type="email"
                value={inviteForm.ownerEmail}
                onChange={(e) => setInviteForm({...inviteForm, ownerEmail: e.target.value})}
                placeholder="owner@example.com"
                className={dynastyTheme.components.input}
                required
              />
            </div>
          </div>

          <div>
            <label className={dynastyTheme.components.label}>
              Personal Message (Optional)
            </label>
            <textarea
              value={inviteForm.personalMessage}
              onChange={(e) => setInviteForm({...inviteForm, personalMessage: e.target.value})}
              placeholder="Add a personal message to include with the invitation..."
              rows={3}
              className={dynastyTheme.components.input}
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              <p>The invitation will include a link to join "{league?.league_name}" and create a team.</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {inviteForm.targetSlot && (
                <button
                  type="button"
                  onClick={() => setInviteForm({...inviteForm, targetSlot: null, personalMessage: ''})}
                  className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
                >
                  Clear Target Slot
                </button>
              )}
              
              <button
                type="submit"
                disabled={inviteLoading}
                className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} flex items-center space-x-2 ${
                  inviteLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {inviteLoading ? (
                  <>
                    <div className={`w-4 h-4 border-2 ${dynastyTheme.classes.text.black} border-t-transparent rounded-full animate-spin`} />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Invitation</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Owner Management Table */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-4`}>
          Owner Management ({activeTeamsCount + pendingInvitesCount}/{maxTeams} Slots)
        </h3>
        
        <div className={`mb-4 p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
          <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
            <strong>Quick Actions:</strong> Click "Create" to add a team name, then click "Invite" to add owner details. 
            Or use the form above for general invitations.
          </p>
        </div>
        
        {ownersLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
              <span className={dynastyTheme.classes.text.white}>Loading owner data...</span>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Slot</th>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Team Name</th>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Owner Name</th>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Owner Email</th>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Status</th>
                  {league?.role === 'commissioner' && (
                    <th className={`text-center p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Commissioner</th>
                  )}
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white} font-semibold`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((owner) => (
                  <tr 
                    key={owner.team_id || owner.invitation_id || `empty-${owner.slot}`} 
                    className={`border-b hover:${dynastyTheme.classes.bg.primaryLight} ${dynastyTheme.classes.transition} ${dynastyTheme.classes.border.neutral}`}
                  >
                    <td className={`p-3 ${dynastyTheme.classes.text.white} font-semibold`}>
                      {owner.slot}
                    </td>
                    
                    <td className="p-3">
                      {renderEditableCell(owner, 'team_name', 'Enter team name...')}
                    </td>
                    
                    <td className="p-3">
                      {renderEditableCell(owner, 'owner_name', 'Enter owner name...')}
                    </td>
                    
                    <td className="p-3">
                      {renderEditableCell(owner, 'owner_email', 'Enter owner email...')}
                    </td>
                    
                    <td className="p-3">
                      <span className={
                        owner.status === 'Active' 
                          ? dynastyTheme.components.badge.success
                          : owner.status === 'Pending'
                          ? dynastyTheme.components.badge.warning
                          : `px-2 py-1 rounded text-xs font-semibold ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white}`
                      }>
                        {owner.status}
                      </span>
                    </td>
                    
                    {league?.role === 'commissioner' && (
                      <td className="p-3 text-center">
                        {owner.status === 'Active' && owner.team_id ? (
                          <input
                            type="checkbox"
                            checked={owner.is_commissioner || false}
                            onChange={() => handleToggleCommissioner(owner.team_id, owner.is_commissioner)}
                            disabled={owner.user_id === user?.sub}
                            className={`w-5 h-5 rounded ${dynastyTheme.classes.text.primary} ${
                              owner.user_id === user?.sub ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            title={
                              owner.user_id === user?.sub 
                                ? "You cannot remove your own commissioner rights" 
                                : owner.is_commissioner 
                                  ? "Remove commissioner rights" 
                                  : "Grant commissioner rights"
                            }
                          />
                        ) : (
                          <span className={dynastyTheme.classes.text.neutralLight}>-</span>
                        )}
                      </td>
                    )}
                    
                    <td className="p-3">
                      {renderActionButtons(owner)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueOwners;
